class QuickOrder {
    constructor() {
      this.debugMode = false;
      this.logPrefix = "[QuickOrder]";

      // Templates
      this.templates = {
        searchResult: document.getElementById("search-result-template"),
      };
  
      // Element-Selektoren
      this.elements = {
        input: document.getElementById("article-number-input"),
        results: document.getElementById("search-results"),
        resultsList: document.getElementById("search-results-list"),
        cartContents: document.getElementById("cart-contents"),
        noResults: document.querySelector(".ds-search-no-results"),
        cartEmpty: document.querySelector(".ds-cart-empty"),
        spinner: document.querySelector(".ds-search-spinner"),
        clearButton: document.getElementById("search-clear"),
        status: document.getElementById("quick-order-status"),
      };
  
      this.debounceDelay = 150;

      // Optionen aus den Section-Einstellungen (Theme-Editor)
      const section = document.querySelector(".quick-order-section");
      this.searchResultLimit = parseInt(section?.dataset.searchLimit, 10) || 5;
      this.minSearchLength = parseInt(section?.dataset.minSearchLength, 10) || 1;
      this.surchargeVariantId = String(section?.dataset.surchargeVariantId || "");

      this.searchAbortController = null;
      this.productCache = new Map();
      this.debounceTimer = null;
      this.lastDisplayedQuery = null;
      this.activeIndex = -1;
      this.optionCounter = 0;
      this.quantityTimers = new Map();
      this.quantityDebounceDelay = 400;
      this.addedBadgeTimers = new WeakMap();
      this.statusTimer = null;

      // CSV-Import
      this.csvElements = {
        drawer: document.getElementById("csv-import-drawer"),
        body: document.getElementById("csv-import-body"),
        dropzone: document.getElementById("csv-dropzone"),
        fileInput: document.getElementById("csv-file-input"),
        fileError: document.getElementById("csv-file-error"),
        filename: document.getElementById("csv-filename"),
        formatWarning: document.getElementById("csv-format-warning"),
        previewSummary: document.getElementById("csv-preview-summary"),
        previewRows: document.getElementById("csv-preview-rows"),
        previewMore: document.getElementById("csv-preview-more"),
        progressBar: document.getElementById("csv-progress-bar"),
        progressLabel: document.getElementById("csv-progress-label"),
        resultSummary: document.getElementById("csv-result-summary"),
        resultNote: document.getElementById("csv-result-note"),
        failedWrapper: document.getElementById("csv-failed-wrapper"),
        failedList: document.getElementById("csv-failed-list"),
        cancelButton: document.getElementById("csv-cancel-button"),
        primaryButton: document.getElementById("csv-primary-button"),
      };
      this.csvImport = { state: "idle", parsed: null, aborted: false };
      this.csvPreviewLimit = 20;
      this.csvResolveConcurrency = 3;

      this.init();
    }
  
    init() {
      this.log("Initialisiere QuickOrder...");
      try {
        this.addEventListeners();
        this.log("Initialisierung abgeschlossen");
      } catch (error) {
        this.logError("Initialisierungsfehler:", error);
      }
    }
  
    addEventListeners() {
      this.elements.input.addEventListener(
        "input",
        this.debounce(this.handleInput.bind(this))
      );
      // Sichtbarkeit des Clear-Buttons sofort (ohne Debounce) aktualisieren
      this.elements.input.addEventListener("input", () =>
        this.toggleClearButton()
      );
      this.elements.input.addEventListener(
        "keydown",
        this.handleInputKeydown.bind(this)
      );
      // Fokus/Klick ins Suchfeld: geschlossenes Dropdown für die
      // vorhandene Eingabe wieder öffnen
      const reopenResults = this.maybeReopenResults.bind(this);
      this.elements.input.addEventListener("focus", reopenResults);
      this.elements.input.addEventListener("click", reopenResults);
      this.elements.clearButton?.addEventListener("click", () => {
        this.clearSearch();
        this.elements.input.focus();
      });
      this.elements.results.addEventListener(
        "click",
        this.handleResultClick.bind(this)
      );
      this.elements.results.addEventListener(
        "keydown",
        this.handleResultKeydown.bind(this)
      );
      this.elements.cartContents.addEventListener(
        "change",
        this.handleQuantityChange.bind(this)
      );
      this.elements.cartContents.addEventListener(
        "click",
        this.handleRemoveClick.bind(this)
      );
      // Klick außerhalb schließt das Dropdown
      document.addEventListener("click", (event) => {
        if (!event.target.closest(".ds-search-wrapper-container")) {
          this.clearResults();
        }
      });

      this.initCsvImport();
    }
  
    // Dropdown wieder öffnen, wenn das Suchfeld eine gültige Eingabe enthält
    maybeReopenResults() {
      const query = this.elements.input.value.trim();
      if (!this.elements.results.hidden) return;
      if (query.length < this.minSearchLength) return;
      this.searchProducts(query);
    }

    async handleInput(event) {
      const query = event.target.value.trim();
      this.log("Sucheingabe:", query);
  
      if (query.length < this.minSearchLength) {
        this.clearResults();
        return;
      }
      await this.searchProducts(query);
    }
  
    async handleResultClick(event) {
      if (this.handleStepperClick(event)) return;

      const target = event.target.closest(".ds-search-result-link");
      if (!target) return;

      const variantId = target.dataset.variantId;
      if (variantId) {
        this.log("Füge Variante hinzu:", variantId);
        await this.addToCart(variantId, this.getRowQuantity(target));
      }
    }

    // −/+-Buttons eines Steppers; gibt true zurück, wenn der Klick behandelt wurde
    handleStepperClick(event) {
      const button = event.target.closest(".ds-qty-btn");
      if (!button) return false;

      const input = button
        .closest(".ds-quantity-stepper")
        ?.querySelector("input[type='number']");
      if (!input || input.disabled) return true;

      const min = parseInt(input.min, 10) || 1;
      const current = parseInt(input.value, 10) || min;
      const delta = button.classList.contains("ds-qty-plus") ? 1 : -1;
      const next = Math.max(min, current + delta);

      if (next !== current) {
        input.value = next;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return true;
    }

    async handleInputKeydown(event) {
      // Pfeiltasten: durch die Ergebnisliste navigieren
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const rows = this.getEnabledRows();
        if (this.elements.results.hidden || !rows.length) return;
        event.preventDefault();
        const delta = event.key === "ArrowDown" ? 1 : -1;
        this.setActiveRow(
          (this.activeIndex + delta + rows.length) % rows.length
        );
        return;
      }

      // Escape: Dropdown schließen
      if (event.key === "Escape") {
        this.clearResults();
        return;
      }

      if (event.key !== "Enter") return;
      event.preventDefault();
      clearTimeout(this.debounceTimer);

      // Enter auf einer per Pfeiltaste markierten Zeile: diese hinzufügen
      if (!this.elements.results.hidden && this.activeIndex >= 0) {
        const link = this.getEnabledRows()
          [this.activeIndex]?.querySelector(".ds-search-result-link");
        if (link?.dataset.variantId) {
          await this.addToCart(link.dataset.variantId, this.getRowQuantity(link));
          return;
        }
      }

      // Sonst: exakten SKU-Treffer direkt in den Warenkorb legen
      const query = this.elements.input.value.trim();
      if (query.length < this.minSearchLength) return;

      if (this.lastDisplayedQuery !== query || this.elements.results.hidden) {
        await this.searchProducts(query);
      }
      await this.addBestMatch(query);
    }

    async handleResultKeydown(event) {
      // Escape: Dropdown schließen, Fokus zurück ins Suchfeld
      if (event.key === "Escape") {
        this.clearResults();
        this.elements.input.focus();
        return;
      }

      // Enter im Mengenfeld einer Ergebniszeile: diese Zeile hinzufügen
      if (event.key !== "Enter") return;
      const input = event.target.closest(".ds-search-result-quantity");
      if (!input) return;
      event.preventDefault();

      const link = input
        .closest(".ds-flex-between")
        ?.querySelector(".ds-search-result-link");
      const variantId = link?.dataset.variantId;
      if (variantId) {
        await this.addToCart(variantId, this.getRowQuantity(link));
      }
    }

    getEnabledRows() {
      return [
        ...this.elements.resultsList.querySelectorAll(
          ".ds-flex-between:not(.ds-disabled)"
        ),
      ];
    }

    setActiveRow(index) {
      const rows = this.getEnabledRows();
      this.activeIndex = index;

      rows.forEach((row, i) => {
        const isActive = i === index;
        row.classList.toggle("is-active", isActive);
        row
          .querySelector(".ds-search-result-link")
          ?.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      const activeRow = rows[index];
      const activeLink = activeRow?.querySelector(".ds-search-result-link");
      if (activeLink?.id) {
        this.elements.input.setAttribute("aria-activedescendant", activeLink.id);
        activeRow.scrollIntoView({ block: "nearest" });
      } else {
        this.elements.input.removeAttribute("aria-activedescendant");
      }
    }

    async addBestMatch(query) {
      const rows = this.getEnabledRows();
      if (!rows.length) return;

      const normalized = query.toLowerCase();
      // Exakter Treffer über SKU oder EAN/Barcode
      const exactRow = rows.find((row) => {
        const sku = row
          .querySelector(".variant-sku")
          ?.textContent.trim()
          .toLowerCase();
        const barcode = (
          row.querySelector(".ds-search-result-link")?.dataset.barcode || ""
        ).toLowerCase();
        return sku === normalized || barcode === normalized;
      });
      const row = exactRow || (rows.length === 1 ? rows[0] : null);
      if (!row) return;

      const link = row.querySelector(".ds-search-result-link");
      const variantId = link?.dataset.variantId;
      if (variantId) {
        await this.addToCart(variantId, this.getRowQuantity(link));
      }
    }

    getRowQuantity(rowChild) {
      const input = rowChild
        .closest(".ds-flex-between")
        ?.querySelector(".ds-search-result-quantity");
      const quantity = parseInt(input?.value, 10);
      return quantity > 0 ? quantity : 1;
    }
  
    handleQuantityChange(event) {
      const input = event.target.closest(".ds-cart-item-quantity");
      if (!input) return;

      const variantId = input.dataset.variantId;
      const quantity = parseInt(input.value, 10);

      // Leere oder ungültige Eingabe: auf letzten bekannten Wert zurücksetzen
      if (!(quantity > 0)) {
        input.value = input.defaultValue;
        return;
      }

      // Schnelle aufeinanderfolgende Änderungen (z. B. Spinner-Klicks) bündeln
      clearTimeout(this.quantityTimers.get(variantId));
      this.quantityTimers.set(
        variantId,
        setTimeout(() => {
          this.quantityTimers.delete(variantId);
          this.updateCartItem(variantId, quantity);
        }, this.quantityDebounceDelay)
      );
    }
  
    async handleRemoveClick(event) {
      if (this.handleStepperClick(event)) return;

      const button = event.target.closest(".ds-cart-item-remove");
      if (!button) return;

      const variantId = button.dataset.variantId;
      // Ausstehendes Mengen-Update für diesen Artikel verwerfen
      clearTimeout(this.quantityTimers.get(variantId));
      this.quantityTimers.delete(variantId);
      await this.updateCartItem(variantId, 0);
    }
  
    async searchProducts(query) {
      // Vorherige Suche abbrechen, damit sich Antworten nicht überholen
      this.searchAbortController?.abort();
      this.searchAbortController = new AbortController();
      const signal = this.searchAbortController.signal;
      this.setSearchLoading(true);

      try {
        const response = await fetch(
          `${window.Shopify.routes.root}search/suggest.json?` +
            new URLSearchParams({
              q: query,
              "resources[type]": "product",
              "resources[limit]": String(this.searchResultLimit),
              "resources[options][unavailable_products]": "show",
              // Suche auch nach SKU und EAN/Barcode
              "resources[options][fields]":
                "title,variants.sku,variants.title,variants.barcode",
            }),
          { signal }
        );

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();

        // Get complete product data for each result
        const productsWithVariants = await Promise.all(
          data.resources.results.products.map(async (product) => {
            try {
              const fullProduct = await this.fetchProduct(product.handle, signal);
              if (!fullProduct) return product;
              return {
                ...product,
                variants: fullProduct.variants,
                available: fullProduct.available,
              };
            } catch (error) {
              if (error.name === "AbortError") throw error;
              return product;
            }
          })
        );

        if (signal.aborted) return;
        this.displayResults(productsWithVariants, query);
        this.lastDisplayedQuery = query;
      } catch (error) {
        if (error.name === "AbortError") return;
        this.logError("Suchfehler:", error);
        this.showError();
      } finally {
        // Spinner nur ausblenden, wenn keine neuere Suche läuft
        if (this.searchAbortController?.signal === signal) {
          this.setSearchLoading(false);
        }
      }
    }

    async fetchProduct(handle, signal) {
      if (this.productCache.has(handle)) {
        return this.productCache.get(handle);
      }

      const response = await fetch(
        `${window.Shopify.routes.root}products/${handle}.js`,
        { signal }
      );
      if (!response.ok) return null;

      const product = await response.json();
      this.productCache.set(handle, product);
      return product;
    }
  
    displayResults(products, query) {
      this.clearResults();

      if (!products.length) {
        this.elements.noResults.hidden = false;
        this.elements.results.hidden = false;
        this.elements.input.setAttribute("aria-expanded", "true");
        return;
      }
  
      products.forEach((product) => {
        const variants =
          product.variants.length > 0
            ? product.variants
            : [
                {
                  id: product.id,
                  sku: product.sku || "N/A",
                  title: product.title,
                  available: product.available,
                  handle: product.handle,
                },
              ];
  
        variants.forEach((variant) =>
          this.addSearchResult(variant, product, query)
        );
      });

      this.elements.results.hidden = false;
      this.elements.input.setAttribute("aria-expanded", "true");
    }
  
    addSearchResult(variant, product, query) {
      const template = this.templates.searchResult.content.cloneNode(true);
      const container = template.querySelector(".ds-flex-between");
      const link = template.querySelector(".ds-search-result-link");
      const productLink = template.querySelector(".ds-link");

      const available = variant.available;

      if (!available) {
        container.classList.add("ds-disabled");
        link.classList.add("ds-disabled");
        productLink.classList.add("ds-disabled");
        link.style.pointerEvents = "none";
        productLink.style.pointerEvents = "none";
        link.setAttribute("tabindex", "-1");
        productLink.setAttribute("tabindex", "-1");
        link.setAttribute("aria-disabled", "true");
        template.querySelector(".ds-search-result-quantity").disabled = true;
        template.querySelector(".variant-badge").hidden = false;
      }

      const image = template.querySelector(".variant-image");
      const imageUrl =
        variant.featured_image?.src ||
        product.featured_image?.url ||
        product.image ||
        "";
      if (imageUrl) {
        image.src = `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}width=80`;
        image.alt = product.title;
        image.hidden = false;
      } else {
        // Leerer Platzhalter, damit die Zeilen bündig bleiben
        const placeholder = document.createElement("span");
        placeholder.className = "ds-img-placeholder";
        placeholder.setAttribute("aria-hidden", "true");
        image.replaceWith(placeholder);
      }

      link.id = `qo-option-${this.optionCounter++}`;
      link.dataset.variantId = variant.id;
      link.dataset.barcode = variant.barcode || "";
      this.setHighlightedText(
        template.querySelector(".variant-sku"),
        variant.sku || "",
        query
      );
      const hasVariantTitle =
        variant.title &&
        variant.title !== "Default Title" &&
        variant.title !== product.title;
      this.setHighlightedText(
        template.querySelector(".variant-title"),
        hasVariantTitle ? `${product.title} - ${variant.title}` : product.title,
        query
      );
      productLink.href = `/products/${product.handle}`;

      this.elements.resultsList.appendChild(template);
    }

    // Suchbegriff im Text mit <mark> hervorheben
    setHighlightedText(element, text, query) {
      element.textContent = "";
      const q = (query || "").trim().toLowerCase();
      if (!q) {
        element.textContent = text;
        return;
      }

      const lower = text.toLowerCase();
      let position = 0;
      let matchIndex;
      while ((matchIndex = lower.indexOf(q, position)) !== -1) {
        element.appendChild(
          document.createTextNode(text.slice(position, matchIndex))
        );
        const mark = document.createElement("mark");
        mark.textContent = text.slice(matchIndex, matchIndex + q.length);
        element.appendChild(mark);
        position = matchIndex + q.length;
      }
      element.appendChild(document.createTextNode(text.slice(position)));
    }
  
    async addToCart(variantId, quantity = 1) {
      const loadingClass = "is-loading";
      const targetRow = this.elements.results
        .querySelector(`[data-variant-id="${variantId}"]`)
        ?.closest(".ds-flex-between");
  
      try {
        if (targetRow) {
          targetRow.classList.add(loadingClass);
        }

        const result = await this.addToCartSilently(variantId, quantity);
        if (!result.ok) {
          throw new Error(result.error);
        }

        await this.updateCart();

        // Feedback: Zeile markieren, Warenkorb-Zeile hervorheben, Statusmeldung
        this.showRowSuccess(targetRow);
        this.flashCartRow(variantId);
        const title = targetRow
          ?.querySelector(".variant-title")
          ?.textContent?.trim();
        const addedText =
          window.theme?.translations?.addedToCart || "Added to your cart!";
        this.showStatus(title ? `${title} – ${addedText}` : addedText);

        // Suchfeld-Inhalt markieren, damit die nächste Eingabe ihn ersetzt;
        // das Dropdown bleibt für weitere Varianten offen
        this.elements.input.focus();
        this.elements.input.select();
      } catch (error) {
        this.logError("Cart Error:", error.message);
        this.showStatus(error.message, "error");
      } finally {
        if (targetRow) {
          targetRow.classList.remove(loadingClass);
        }
      }
    }

    // Reiner /cart/add.js-Aufruf ohne UI-Nebeneffekte (auch vom CSV-Import genutzt)
    async addToCartSilently(variantId, quantity = 1) {
      try {
        const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: quantity }],
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          return {
            ok: false,
            error: data.description || "Failed to add item to cart",
          };
        }
        return { ok: true };
      } catch (error) {
        return { ok: false, error: error.message };
      }
    }

    // Ergebniszeile kurz als "hinzugefügt" markieren
    showRowSuccess(row) {
      if (!row) return;

      row.classList.add("is-added");
      let badge = row.querySelector(".ds-added-badge");
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "ds-search-result-badge ds-added-badge";
        badge.textContent =
          window.theme?.translations?.addedToCart || "Added to your cart!";
        row.querySelector(".ds-search-result-link")?.appendChild(badge);
      }

      clearTimeout(this.addedBadgeTimers.get(row));
      this.addedBadgeTimers.set(
        row,
        setTimeout(() => {
          row.classList.remove("is-added");
          badge.remove();
        }, 2000)
      );
    }

    // Hinzugefügte bzw. geänderte Warenkorb-Zeile kurz aufblinken lassen
    flashCartRow(variantId) {
      const cartRow = this.elements.cartContents
        .querySelector(`[data-variant-id="${variantId}"]`)
        ?.closest(".ds-cart-item");
      if (!cartRow) return;

      cartRow.classList.add("is-flash");
      cartRow.addEventListener(
        "animationend",
        () => cartRow.classList.remove("is-flash"),
        { once: true }
      );
    }

    // Statusmeldung unterm Titel anzeigen (aria-live, ersetzt alert())
    showStatus(message, type = "success") {
      const status = this.elements.status;
      if (!status) {
        if (type === "error") alert(message);
        return;
      }

      status.textContent = message;
      status.classList.toggle("ds-qo-status--error", type === "error");
      status.hidden = false;

      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        status.hidden = true;
      }, 4000);
    }
  
    async updateCartItem(variantId, quantity) {
      const loadingClass = "is-loading";
      const targetRow = this.elements.cartContents
        .querySelector(`[data-variant-id="${variantId}"]`)
        ?.closest(".ds-cart-item");
  
      try {
        if (targetRow) {
          targetRow.classList.add(loadingClass);
        }
  
        const response = await fetch(
          window.Shopify.routes.root + "cart/change.js",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: variantId, quantity: quantity }),
          }
        );
  
        const data = await response.json();
  
        if (!response.ok) {
          throw new Error(data.description || "Failed to update cart");
        }
  
        await this.updateCart();
      } catch (error) {
        this.logError("Update Error:", error.message);
        this.showStatus(error.message, "error");
        // Anzeige auf den Serverstand zurücksetzen
        await this.updateCart();
      } finally {
        if (targetRow) {
          targetRow.classList.remove(loadingClass);
        }
      }
    }
  
    async updateCart() {
      try {
        const response = await fetch(window.Shopify.routes.root + "cart.js");
        if (!response.ok) {
          throw new Error("Failed to fetch cart");
        }
  
        const cart = await response.json();
        const visibleItems = cart.items.filter(
          (item) => String(item.variant_id) !== this.surchargeVariantId
        );
        const cartContents = this.elements.cartContents;
  
        if (!cartContents) return;
  
        if (visibleItems.length === 0) {
          cartContents.innerHTML = `
            <div class="ds-cart-empty">
              ${window.theme?.translations?.cartEmpty || "Your cart is empty"}
            </div>
          `;
          return;
        }
  
        // Create the cart table structure
        const cartHTML = `
          <table class="ds-cart-table">
            <thead>
              <tr>
                <th class="ds-cart-header ds-cart-header-article">${window.theme?.translations?.cartOrderArticle || "Article"}</th>
                <th class="ds-cart-header ds-cart-header-quantity"><span class="ds-cart-header-quantity-label">${window.theme?.translations?.cartOrderQuantity || "Quantity"}</span></th>
              </tr>
            </thead>
            <tbody id="cart-items">
              ${visibleItems
                .map((item) => {
                  const esc = this.escapeHtml.bind(this);
                  const title = `${(item.product_title || item.title || "").trim()}${
                    item.variant_title && item.variant_title !== "Default Title"
                      ? ` - ${item.variant_title.trim()}`
                      : ""
                  }${item.sku ? ` - ${item.sku.trim()}` : ""}`;
                  const image = item.image
                    ? `<img
                          width="40"
                          height="40"
                          src="${esc(item.image)}"
                          alt="${esc(item.title || "")}"
                          class="ds-cart-item-img item-image">`
                    : `<span class="ds-img-placeholder" aria-hidden="true"></span>`;
                  return `
                <tr class="ds-cart-item">
                  <td>
                    <div class="ds-cart-item-detail">
                      <a class="item-url" href="${esc(item.url || "")}">
                        ${image}
                      </a>
                      <span class="ds-cart-item-title">
                        <a class="item-title item-url" href="${esc(item.url || "")}">
                          ${esc(title)}
                        </a>
                      </span>
                    </div>
                  </td>
                  <td class="ds-quantity-remove-wrapper">
                    <div class="ds-quantity-stepper">
                      <button
                        type="button"
                        class="ds-qty-btn ds-qty-minus"
                        aria-label="${esc(window.theme?.translations?.decreaseQuantity || "Decrease quantity")}">&minus;</button>
                      <input
                        type="number"
                        value="${item.quantity}"
                        min="1"
                        class="ds-cart-item-quantity"
                        data-variant-id="${item.variant_id}"
                        aria-label="${esc(window.theme?.translations?.cartOrderQuantity || "Quantity")}">
                      <button
                        type="button"
                        class="ds-qty-btn ds-qty-plus"
                        aria-label="${esc(window.theme?.translations?.increaseQuantity || "Increase quantity")}">+</button>
                    </div>
                    <button
                      class="ds-cart-item-remove"
                      data-variant-id="${item.variant_id}"
                      aria-label="${esc(window.theme?.translations?.cartOrderRemove || "Remove")}">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
                        <path d="M3 6h18"/>
                        <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              `;
                })
                .join("")}
            </tbody>
          </table>
          <div class="ds-cart-checkout">
            <a class="button" href="${window.Shopify.routes.root}cart">
              ${window.theme?.translations?.viewCart || "View Cart"}
            </a>
          </div>
        `;
  
        cartContents.innerHTML = cartHTML;
      } catch (error) {
        this.logError("Cart Update Error:", error.message);
      }
    }

    // Hilfsfunktionen
    escapeHtml(value) {
      return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
    }

    debounce(fn) {
      return (...args) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(
          () => fn.apply(this, args),
          this.debounceDelay
        );
      };
    }

    clearSearch() {
      this.elements.input.value = "";
      this.toggleClearButton();
      this.clearResults();
    }

    toggleClearButton() {
      if (this.elements.clearButton) {
        this.elements.clearButton.hidden = this.elements.input.value.length === 0;
      }
    }

    setSearchLoading(loading) {
      if (this.elements.spinner) {
        this.elements.spinner.hidden = !loading;
      }
    }

    clearResults() {
      this.elements.resultsList.innerHTML = "";
      this.elements.results.hidden = true;
      this.elements.noResults.hidden = true;
      this.lastDisplayedQuery = null;
      this.activeIndex = -1;
      this.optionCounter = 0;
      this.elements.input.setAttribute("aria-expanded", "false");
      this.elements.input.removeAttribute("aria-activedescendant");
    }

    showError() {
      this.elements.noResults.hidden = false;
      this.elements.results.hidden = false;
      this.elements.input.setAttribute("aria-expanded", "true");
    }
  
    /* ------------------------------------------------------------------
     * CSV-Import
     * ------------------------------------------------------------------ */

    initCsvImport() {
      const els = this.csvElements;
      if (!els.drawer || !els.fileInput) return;

      els.fileInput.addEventListener(
        "change",
        this.handleCsvFileSelected.bind(this)
      );

      // Drag & Drop auf die Dropzone (nur im Idle-Schritt sichtbar)
      if (els.dropzone) {
        let dragDepth = 0;
        els.dropzone.addEventListener("dragenter", (event) => {
          event.preventDefault();
          dragDepth++;
          els.dropzone.classList.add("is-dragover");
        });
        els.dropzone.addEventListener("dragover", (event) => {
          event.preventDefault();
        });
        els.dropzone.addEventListener("dragleave", () => {
          // Zähler statt relatedTarget: dragleave feuert auch beim
          // Überfahren von Kind-Elementen
          if (--dragDepth <= 0) {
            dragDepth = 0;
            els.dropzone.classList.remove("is-dragover");
          }
        });
        els.dropzone.addEventListener("drop", (event) => {
          event.preventDefault();
          dragDepth = 0;
          els.dropzone.classList.remove("is-dragover");
          const file = event.dataTransfer?.files?.[0];
          if (file) this.processCsvFile(file);
        });
        // Klick auf die Dropzone öffnet den Dateidialog (das Label
        // macht das bereits selbst, daher dort nicht doppelt auslösen)
        els.dropzone.addEventListener("click", (event) => {
          if (event.target.closest(".ds-csv-file-label")) return;
          els.fileInput.click();
        });
      }

      els.primaryButton.addEventListener("click", () => {
        if (this.csvImport.state === "preview") {
          this.startCsvImport();
        } else if (this.csvImport.state === "done") {
          this.hideCsvDrawer();
        }
      });

      els.cancelButton.addEventListener("click", () => {
        if (this.csvImport.state === "importing") {
          // Kooperativer Abbruch: laufende Zeile darf fertig werden
          this.csvImport.aborted = true;
          els.cancelButton.disabled = true;
        } else if (this.csvImport.state === "preview") {
          this.resetCsvImport();
        } else {
          this.hideCsvDrawer();
        }
      });

      // Während des Imports darf der Drawer nicht geschlossen werden
      // (X-Button, Overlay-Klick und ESC schließen rein attributgesteuert)
      els.drawer.addEventListener("dialog:after-hide", () => {
        if (this.csvImport.state === "importing") {
          els.drawer.setAttribute("open", "");
        }
      });

      // Beim erneuten Öffnen nach abgeschlossenem Import zurücksetzen
      els.drawer.addEventListener("dialog:after-show", () => {
        if (this.csvImport.state === "done") {
          this.resetCsvImport();
        }
      });
    }

    hideCsvDrawer() {
      const drawer = this.csvElements.drawer;
      if (typeof drawer.hide === "function") {
        drawer.hide();
      } else {
        drawer.removeAttribute("open");
      }
    }

    // Übersetzung aus window.theme.translations mit [param]-Platzhaltern
    csvT(key, params = {}) {
      let text = window.theme?.translations?.[key] || key;
      Object.entries(params).forEach(([name, value]) => {
        text = text.replaceAll(`[${name}]`, String(value));
      });
      return text;
    }

    setCsvState(state) {
      this.csvImport.state = state;
      const els = this.csvElements;

      els.body.dataset.csvState = state;
      els.body.querySelectorAll(".ds-csv-step").forEach((step) => {
        step.hidden = !step.classList.contains(`ds-csv-step--${state}`);
      });
      els.drawer.classList.toggle("is-importing", state === "importing");

      els.cancelButton.disabled = false;
      switch (state) {
        case "idle":
          els.primaryButton.hidden = true;
          els.cancelButton.hidden = false;
          els.cancelButton.textContent = this.csvT("csvImportClose");
          break;
        case "preview":
          els.primaryButton.hidden = false;
          els.primaryButton.textContent = this.csvT("csvImportStartImport");
          els.cancelButton.hidden = false;
          els.cancelButton.textContent = this.csvT("csvImportCancel");
          break;
        case "importing":
          els.primaryButton.hidden = true;
          els.cancelButton.hidden = false;
          els.cancelButton.textContent = this.csvT("csvImportCancel");
          break;
        case "done":
          els.primaryButton.hidden = false;
          els.primaryButton.textContent = this.csvT("csvImportClose");
          els.cancelButton.hidden = true;
          break;
      }
    }

    resetCsvImport() {
      const els = this.csvElements;
      this.csvImport = { state: "idle", parsed: null, aborted: false };
      els.fileInput.value = "";
      els.fileError.hidden = true;
      els.fileError.textContent = "";
      els.filename.textContent = "";
      els.formatWarning.hidden = true;
      els.previewSummary.textContent = "";
      els.previewRows.innerHTML = "";
      els.previewMore.hidden = true;
      els.progressLabel.textContent = "";
      els.progressBar?.setAttribute("aria-valuenow", "0");
      els.progressBar?.setAttribute("aria-valuemax", "1");
      els.resultSummary.textContent = "";
      els.resultSummary.classList.remove("ds-csv-result-summary--error");
      els.resultNote.hidden = true;
      els.resultNote.textContent = "";
      els.failedWrapper.hidden = true;
      els.failedList.innerHTML = "";
      this.setCsvState("idle");
    }

    async handleCsvFileSelected(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      await this.processCsvFile(file);
    }

    async processCsvFile(file) {
      const els = this.csvElements;
      els.fileError.hidden = true;

      let text;
      try {
        text = await file.text();
      } catch (error) {
        this.logError("CSV-Lesefehler:", error);
        els.fileError.textContent = this.csvT("csvImportParseError");
        els.fileError.hidden = false;
        return;
      }

      let parsed;
      try {
        parsed = this.parseCsv(text);
      } catch (error) {
        this.logError("CSV-Parsefehler:", error);
        els.fileError.textContent = this.csvT("csvImportParseError");
        els.fileError.hidden = false;
        return;
      }

      if (!parsed.rows.length) {
        els.fileError.textContent = this.csvT("csvImportNoRowsFound");
        els.fileError.hidden = false;
        return;
      }

      this.csvImport = { state: "preview", parsed, file, aborted: false };
      this.renderCsvPreview(file.name, parsed);
      this.setCsvState("preview");
    }

    renderCsvPreview(fileName, parsed) {
      const els = this.csvElements;
      const validRows = parsed.rows.filter((row) => row.sku);
      const invalidCount = parsed.rows.length - validRows.length;

      els.filename.textContent = this.csvT("csvImportFileSelected", {
        filename: fileName,
      });
      // Vermutlich kein CSV (z. B. umbenannte Datei): warnen, aber zulassen
      els.formatWarning.hidden = invalidCount <= parsed.rows.length / 2;
      els.previewSummary.textContent = this.csvT("csvImportPreviewSummary", {
        count: validRows.length,
      });

      els.previewRows.innerHTML = "";
      validRows.slice(0, this.csvPreviewLimit).forEach((row) => {
        const tr = document.createElement("tr");
        const skuCell = document.createElement("td");
        skuCell.textContent = row.sku;
        const qtyCell = document.createElement("td");
        qtyCell.textContent = String(row.quantity);
        tr.append(skuCell, qtyCell);
        els.previewRows.appendChild(tr);
      });

      const remaining = validRows.length - this.csvPreviewLimit;
      els.previewMore.hidden = remaining <= 0;
      if (remaining > 0) {
        els.previewMore.textContent = this.csvT("csvImportPreviewMore", {
          count: remaining,
        });
      }
    }

    /* --------------------------- CSV-Parser --------------------------- */

    stripBom(text) {
      return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
    }

    // Häufigstes Trennzeichen (außerhalb von Anführungszeichen) gewinnt
    detectDelimiter(lines) {
      const counts = { ";": 0, ",": 0, "\t": 0 };
      lines.slice(0, 5).forEach((line) => {
        let inQuotes = false;
        for (const char of line) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (!inQuotes && char in counts) {
            counts[char]++;
          }
        }
      });
      return [";", ",", "\t"].reduce((best, candidate) =>
        counts[candidate] > counts[best] ? candidate : best
      );
    }

    // Eine CSV-Zeile in Felder zerlegen ("a;b", ""-Escapes)
    parseCsvLine(line, delimiter) {
      const fields = [];
      let field = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (inQuotes) {
          if (char === '"') {
            if (line[i + 1] === '"') {
              field += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            field += char;
          }
        } else if (char === '"') {
          inQuotes = true;
        } else if (char === delimiter) {
          fields.push(field);
          field = "";
        } else {
          field += char;
        }
      }
      fields.push(field);
      return fields;
    }

    // Kopfzeile: Mengenspalte ist Text (z. B. "Menge"/"Anzahl"/"Qty") –
    // damit sind bei zweispaltigen Dateien alle Spaltennamen abgedeckt.
    // Bei einspaltigen Dateien entscheidet ein Spaltennamen-Keyword;
    // kurze Tokens (Nr, No, ID, …) nur mit Wortgrenze, und generell nur
    // ohne Ziffern, damit echte Artikelnummern, die zufällig "sku"/"nr"
    // enthalten, nicht verworfen werden.
    looksLikeHeaderRow(entry) {
      const quantity = entry.rawQuantity;
      if (quantity !== "") return !/^\d+([.,]\d+)?$/.test(quantity);
      return (
        !/\d/.test(entry.sku) &&
        (/sku|ean|gtin|upc|asin|mpn|artikel|barcode|nummer|code|item|part|produkt|product|material|referenz|position/i.test(
          entry.sku
        ) ||
          /(^|[^a-z])(nr|no|id|ref|pos|art)\.?([^a-z]|$)/i.test(entry.sku) ||
          // Komposita wie "ArtNr."/"Bestellnr."
          /nr\.?$/i.test(entry.sku))
      );
    }

    // Fehlende/ungültige Menge → 1; Excel-Dezimalwerte ("5,0") tolerieren
    parseCsvQuantity(raw) {
      const value = (raw || "").trim();
      if (!value) return 1;
      const quantity = parseInt(value.replace(/[.,]\d*$/, ""), 10);
      return Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
    }

    parseCsv(text) {
      const lines = this.stripBom(text).split(/\r\n|\r|\n/);
      const nonEmpty = [];
      lines.forEach((line, index) => {
        if (line.trim() !== "") {
          nonEmpty.push({ line, lineNumber: index + 1 });
        }
      });
      if (!nonEmpty.length) {
        return { rows: [], hadHeader: false, delimiter: ";" };
      }

      const delimiter = this.detectDelimiter(nonEmpty.map((entry) => entry.line));
      let entries = nonEmpty.map(({ line, lineNumber }) => {
        const fields = this.parseCsvLine(line, delimiter);
        return {
          sku: (fields[0] || "").trim(),
          rawQuantity: (fields[1] || "").trim(),
          lineNumber,
        };
      });

      let hadHeader = false;
      if (this.looksLikeHeaderRow(entries[0])) {
        entries = entries.slice(1);
        hadHeader = true;
      }

      const rows = entries.map((entry) => ({
        sku: entry.sku,
        quantity: this.parseCsvQuantity(entry.rawQuantity),
        lineNumber: entry.lineNumber,
      }));
      return { rows, hadHeader, delimiter };
    }

    /* ------------------------ Auflösung & Import ---------------------- */

    // SKU/EAN exakt zu einer Variante auflösen (Suche + Exakt-Match)
    async resolveVariant(query) {
      const response = await fetch(
        `${window.Shopify.routes.root}search/suggest.json?` +
          new URLSearchParams({
            q: query,
            "resources[type]": "product",
            "resources[limit]": "3",
            "resources[options][unavailable_products]": "show",
            "resources[options][fields]":
              "title,variants.sku,variants.title,variants.barcode",
          })
      );
      if (!response.ok) {
        return { error: "cart_error", message: `HTTP ${response.status}` };
      }

      const data = await response.json();
      const products = data.resources?.results?.products || [];
      const normalized = query.trim().toLowerCase();
      const matches = [];

      for (const product of products) {
        let fullProduct;
        try {
          fullProduct = await this.fetchProduct(product.handle);
        } catch (error) {
          this.logError("Produkt-Fetch-Fehler:", error);
          continue;
        }
        if (!fullProduct) continue;

        for (const variant of fullProduct.variants || []) {
          const sku = (variant.sku || "").toLowerCase();
          const barcode = (variant.barcode || "").toLowerCase();
          if (sku === normalized || barcode === normalized) {
            matches.push({ variant, product: fullProduct });
          }
        }
      }

      const uniqueVariantIds = new Set(matches.map((m) => m.variant.id));
      if (uniqueVariantIds.size === 0) return { error: "not_found" };
      if (uniqueVariantIds.size > 1) return { error: "ambiguous" };
      if (!matches[0].variant.available) return { error: "unavailable" };
      return matches[0];
    }

    // Simpler Worker-Pool für parallele (read-only) Auflösungen
    async runPool(items, limit, worker) {
      const results = new Array(items.length);
      let next = 0;
      const runners = Array.from(
        { length: Math.min(limit, items.length) },
        async () => {
          while (next < items.length) {
            if (this.csvImport.aborted) return;
            const index = next++;
            results[index] = await worker(items[index], index);
          }
        }
      );
      await Promise.all(runners);
      return results;
    }

    async startCsvImport() {
      this.csvImport.aborted = false;
      this.setCsvState("importing");
      try {
        await this.runCsvImport();
      } catch (error) {
        this.logError("CSV-Import-Fehler:", error);
        this.showStatus(error.message, "error");
        this.setCsvState("done");
      }
    }

    async runCsvImport() {
      const failedRows = [];

      // Duplikate zusammenfassen (case-insensitiv), Mengen summieren
      const deduped = new Map();
      this.csvImport.parsed.rows.forEach((row) => {
        if (!row.sku) {
          failedRows.push({ ...row, reason: "invalid_row" });
          return;
        }
        const key = row.sku.toLowerCase();
        const existing = deduped.get(key);
        if (existing) {
          existing.quantity += row.quantity;
        } else {
          deduped.set(key, { ...row });
        }
      });

      const entries = [...deduped.values()];
      const total = entries.length;
      let settled = 0;
      let success = 0;
      this.updateCsvProgress(0, total);

      // Phase A: SKUs/EANs parallel auflösen (nur Lesezugriffe)
      const resolved = await this.runPool(
        entries,
        this.csvResolveConcurrency,
        async (entry) => {
          let result;
          try {
            result = await this.resolveVariant(entry.sku);
          } catch (error) {
            result = { error: "cart_error", message: error.message };
          }
          if (result.error) {
            failedRows.push({
              ...entry,
              reason: result.error,
              message: result.message,
            });
            this.updateCsvProgress(++settled, total);
            return null;
          }
          return { entry, variant: result.variant };
        }
      );

      // Phase B: sequenziell in den Warenkorb legen (einzeln, damit pro
      // Position berichtet werden kann und sich Cart-Calls nicht überholen)
      for (const item of resolved) {
        if (this.csvImport.aborted) break;
        if (!item) continue;

        const result = await this.addToCartSilently(
          item.variant.id,
          item.entry.quantity
        );
        if (result.ok) {
          success++;
        } else {
          failedRows.push({
            ...item.entry,
            reason: "cart_error",
            message: result.error,
          });
        }
        this.updateCsvProgress(++settled, total);
      }

      const skipped = total - settled;
      await this.updateCart();
      this.renderCsvResult({
        success,
        failedRows,
        skipped,
        aborted: this.csvImport.aborted,
      });
      this.setCsvState("done");
    }

    updateCsvProgress(current, total) {
      const els = this.csvElements;
      els.progressBar?.setAttribute("aria-valuemax", String(Math.max(total, 1)));
      els.progressBar?.setAttribute("aria-valuenow", String(current));
      els.progressLabel.textContent = this.csvT("csvImportProgress", {
        current,
        total,
      });
    }

    csvReasonLabel(row) {
      const keys = {
        not_found: "csvImportReasonNotFound",
        ambiguous: "csvImportReasonAmbiguous",
        unavailable: "csvImportReasonUnavailable",
        cart_error: "csvImportReasonCartError",
        invalid_row: "csvImportReasonInvalidRow",
      };
      const label = this.csvT(keys[row.reason] || "csvImportReasonCartError");
      return row.reason === "cart_error" && row.message
        ? `${label}: ${row.message}`
        : label;
    }

    renderCsvResult({ success, failedRows, skipped, aborted }) {
      const els = this.csvElements;
      const failed = failedRows.length;

      els.resultSummary.textContent =
        failed === 0 && !aborted
          ? this.csvT("csvImportResultSummaryAllOk", { success })
          : this.csvT("csvImportResultSummary", { success, failed });
      els.resultSummary.classList.toggle(
        "ds-csv-result-summary--error",
        failed > 0
      );

      els.resultNote.hidden = !aborted;
      if (aborted) {
        const abortedNote = this.csvT("csvImportAbortedNote");
        els.resultNote.textContent =
          skipped > 0
            ? `${abortedNote} ${this.csvT("csvImportSkippedNote", { count: skipped })}`
            : abortedNote;
      }

      els.failedList.innerHTML = "";
      els.failedWrapper.hidden = failed === 0;
      failedRows
        .sort((a, b) => a.lineNumber - b.lineNumber)
        .forEach((row) => {
          const li = document.createElement("li");
          const position = document.createElement("span");
          position.className = "ds-csv-failed-position";
          position.textContent = `${this.csvT("csvImportLine", { number: row.lineNumber })} – ${row.sku || "?"} (${row.quantity}×)`;
          const reason = document.createElement("span");
          reason.className = "ds-csv-failed-reason";
          reason.textContent = this.csvReasonLabel(row);
          li.append(position, reason);
          els.failedList.appendChild(li);
        });
    }

    log(...messages) {
      if (this.debugMode) console.log(this.logPrefix, ...messages);
    }
  
    logError(...messages) {
      if (this.debugMode) console.error(this.logPrefix, ...messages);
    }
  }
  
  // Initialisierung
  if (document.getElementById("article-number-input")) {
    window.QuickOrder = new QuickOrder();
  }
